import React, { useEffect, useState, useRef, useCallback } from "react";
import HTMLFlipBook from "react-pageflip";
import { pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import "./Flipbook.css";
import { getPage as cacheGetPage, setPage as cacheSetPage, getMeta as cacheGetMeta, setMeta as cacheSetMeta } from "../utils/pageCache";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

// Estilos dos botões – visual minimalista (sem Tailwind)
const buttonStyles = {
  base: {
    padding: "6px 10px",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: 600,
    transition: "background-color .15s ease, color .15s ease, border-color .15s ease, transform .1s ease",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    backgroundColor: "#ffffff",
    color: "#111827"
  },
  navigation: {
    backgroundColor: "#f9fafb",
    borderColor: "#e5e7eb",
    color: "#111827"
  },
  zoom: {
    backgroundColor: "#f8fafc",
    borderColor: "#e2e8f0",
    color: "#0f172a",
    fontWeight: 700,
    padding: "6px 8px"
  },
  reset: (active) => ({
    backgroundColor: active ? "#e0f2fe" : "#f1f5f9",
    borderColor: active ? "#bae6fd" : "#e2e8f0",
    color: active ? "#0c4a6e" : "#334155",
    fontWeight: 700,
    padding: "6px 8px",
    opacity: active ? 1 : 0.6
  }),
  fullscreen: (active) => ({
    backgroundColor: active ? "#ecfdf5" : "#f9fafb",
    borderColor: active ? "#d1fae5" : "#e5e7eb",
    color: active ? "#065f46" : "#111827"
  })
};

// Componente de Loading
const LoadingOverlay = ({ backgroundColor }) => (
  <div style={{
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor,
    zIndex: 1000,
  }}>
    <div style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "1rem"
    }}>
      <div style={{
        width: "40px",
        height: "40px",
        border: "3px solid #f3f3f3",
        borderTop: "3px solid #3498db",
        borderRadius: "50%",
        animation: "spin 1s linear infinite"
      }} />
      <p style={{ color: "#666", fontSize: "14px" }}>Carregando PDF...</p>
    </div>
  </div>
);

const FlipbookViewer = ({ pdfUrl, pdfData, pageImages, backgroundColor = "#f5f5f5", backgroundImage }) => {
  const [cacheKey, setCacheKey] = useState(null);
  const [pages, setPages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pagesReady, setPagesReady] = useState(false);
  const [scale, setScale] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isTwoPageMode, setIsTwoPageMode] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [viewport, setViewport] = useState({ width: window.innerWidth, height: window.innerHeight });
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const bookRef = useRef(null);
  const containerRef = useRef(null);

  const isSmallScreen = React.useMemo(() => viewport.width < 768, [viewport.width]);
  const effectiveIsTwoPageMode = isSmallScreen ? false : isTwoPageMode;

  useEffect(() => {
    const computeCacheKey = () => {
      if (pdfUrl && typeof pdfUrl === 'string') return `url:${pdfUrl}`;
      if (pdfData && (pdfData.length || pdfData.byteLength)) return `buf:${(pdfData.length || pdfData.byteLength)}`;
      return null;
    };

    const preloadImages = async (arr) => {
      await Promise.all(arr.map((url) => new Promise((resolve) => {
        const img = new Image();
        img.onload = resolve;
        img.onerror = resolve; // Resolve também em caso de erro para não travar o processo
        img.src = url;
      })));
    };

    const load = async () => {
      // Reset states for new PDF
      setIsLoading(true);
      setPagesReady(false);
      setPages([]);

      // ==================================================================
      // INÍCIO DA CORREÇÃO: Linha 138 (aproximadamente)
      // Prioridade máxima para imagens pré-renderizadas do backend.
      // Se `pageImages` existir, use-as e encerre a função aqui.
      // ==================================================================
      if (pageImages && Array.isArray(pageImages) && pageImages.length > 0) {
        try {
          console.log("Modo de renderização: Usando imagens pré-renderizadas do backend.");
          await preloadImages(pageImages);
          setPages(pageImages);
          setTimeout(() => { setPagesReady(true); setIsLoading(false); }, 100);
          return; // <-- IMPORTANTE: Impede a re-renderização pelo pdf.js
        } catch (e) {
          console.warn('Falha ao carregar imagens do backend, tentando renderizar via pdf.js como fallback.', e);
          // Se falhar, o código continuará para a lógica de fallback abaixo.
        }
      }

      // O código abaixo só será executado se `pageImages` estiver vazio ou falhar.
      console.log("Modo de renderização: Usando pdf.js no cliente (fallback).");

      const key = computeCacheKey();
      if (!key) {
        setIsLoading(false);
        return;
      }
      setCacheKey(key);

      let cachedMeta = null;
      try { cachedMeta = await cacheGetMeta(key); } catch {}

      if (cachedMeta?.numPages > 0) {
        const cachedPages = [];
        for (let i = 0; i < cachedMeta.numPages; i++) {
          const p = await cacheGetPage(key, i);
          if (!p) { cachedPages.length = 0; break; }
          cachedPages.push(p);
        }
        if (cachedPages.length === cachedMeta.numPages) {
          await preloadImages(cachedPages);
          setPages(cachedPages);
          setTimeout(() => { setPagesReady(true); setIsLoading(false); }, 100);
          return;
        }
      }

      let src;
      if (pdfData) {
        try {
          const buffer = pdfData.buffer ? pdfData.buffer : pdfData;
          src = { data: new Uint8Array(buffer.slice(0)) };
        } catch (e) {
          console.error('Failed to prepare PDF data for rendering', e);
          setIsLoading(false);
          return;
        }
      } else {
        src = pdfUrl;
      }

      try {
        const pdf = await pdfjs.getDocument(src).promise;
        const numPages = pdf.numPages;
        const renderedPages = [];

        for (let i = 1; i <= numPages; i++) {
          const page = await pdf.getPage(i);
          const viewportPage = page.getViewport({ scale: 2.0 });
          const canvas = document.createElement('canvas');
          canvas.width = viewportPage.width;
          canvas.height = viewportPage.height;
          const context = canvas.getContext('2d');
          await page.render({ canvasContext: context, viewport: viewportPage }).promise;
          const dataUrl = canvas.toDataURL('image/jpeg');
          renderedPages.push(dataUrl);
          try { await cacheSetPage(key, i - 1, dataUrl); } catch {}
        }

        try { await cacheSetMeta(key, { numPages }); } catch {}

        await preloadImages(renderedPages);
        setPages(renderedPages);
        setTimeout(() => { setPagesReady(true); setIsLoading(false); }, 100);
      } catch (error) {
        console.error("Erro ao renderizar PDF com pdf.js:", error);
        setIsLoading(false);
      }
    };

    load();
  }, [pdfUrl, pdfData, pageImages]);

// ... O restante do arquivo permanece o mesmo ...
// (useEffect para keydown, resize, etc. não precisam de alteração)

  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
        case "ArrowLeft":
          e.preventDefault();
          bookRef.current?.pageFlip().flipPrev();
          break;
        case "ArrowRight":
          e.preventDefault();
          bookRef.current?.pageFlip().flipNext();
          break;
        case "f":
        case "F":
          e.preventDefault();
          toggleFullscreen();
          break;
        case "+":
        case "=":
          e.preventDefault();
          handleZoomIn();
          break;
        case "-":
          e.preventDefault();
          handleZoomOut();
          break;
        default:
          break;
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    let resizeTimer;

    const doUpdate = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
      updateFlipbookLayout();
      setIsResizing(false);
    };

    const handleResize = () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      setIsResizing(true);
      resizeTimer = setTimeout(doUpdate, 120);
    };

    const handleOrientation = () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      setIsResizing(true);
      resizeTimer = setTimeout(doUpdate, 260);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientation);

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => handleResize());
      if (containerRef.current) resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientation);
      if (resizeObserver) resizeObserver.disconnect();
      if (resizeTimer) clearTimeout(resizeTimer);
    };
  }, []);

  useEffect(() => {
    if (isDragging && scale > 1) {
      const handleMouseMove = (e) => {
        setPanOffset({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y
        });
      };
      const handleMouseUp = () => setIsDragging(false);

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart, scale]);
  
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      setTimeout(() => {
        setViewport({ width: window.innerWidth, height: window.innerHeight });
      }, 100);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const handleZoomIn = () => {
    setScale((prev) => {
      const newScale = Math.min(prev + 0.2, 3);
      if (newScale !== prev) setPanOffset({ x: 0, y: 0 });
      return newScale;
    });
  };

  const handleZoomOut = () => {
    setScale((prev) => {
      const newScale = Math.max(prev - 0.2, 0.5);
      if (newScale !== prev) setPanOffset({ x: 0, y: 0 });
      return newScale;
    });
  };

  const handleZoomReset = () => {
    setScale(1);
    setPanOffset({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (scale > 1) {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
      setDragStart({
        x: e.clientX - panOffset.x,
        y: e.clientY - panOffset.y
      });
    }
  };

  const handleTouchStart = (e) => {
    if (scale > 1 && e.touches && e.touches.length === 1) {
      setIsDragging(true);
      const touch = e.touches[0];
      setDragStart({ x: touch.clientX - panOffset.x, y: touch.clientY - panOffset.y });
    }
  };

  const handleTouchMove = (e) => {
    if (scale > 1 && isDragging && e.touches && e.touches.length === 1) {
      e.preventDefault();
      e.stopPropagation();
      const touch = e.touches[0];
      setPanOffset({ x: touch.clientX - dragStart.x, y: touch.clientY - dragStart.y });
    }
  };

  const handleTouchEnd = () => {
    if (isDragging) setIsDragging(false);
  };

  const computeBookSize = useCallback(() => {
    if (isSmallScreen) {
      const maxWidth = Math.min(viewport.width * 0.95, 400);
      const maxHeight = Math.min(viewport.height * 0.7, 600);
      const aspect = 3 / 4;
      
      let width = maxWidth;
      let height = Math.round(width / aspect);
      
      if (height > maxHeight) {
        height = maxHeight;
        width = Math.round(height * aspect);
      }
      
      return { width, height };
    }
    
    const maxWidth = Math.min(1200, viewport.width * 0.95);
    const maxHeightPx = Math.min(1400, Math.round(viewport.height * 0.85));
    const aspect = 2 / 3;

    let height = maxHeightPx;
    let width = Math.round(height * aspect);

    if (width > maxWidth) {
      width = maxWidth;
      height = Math.round(width / aspect);
    }

    return { width, height };
  }, [viewport, isSmallScreen]);

  const getPageThicknessClass = () => {
    if (pages.length === 0 || isSmallScreen) return '';
    
    const totalPages = pages.length;
    if (currentPage === 0) return 'pages-right';
    
    const switchPoint = Math.max(Math.floor(totalPages * 0.6), totalPages - 3);
    if (currentPage >= switchPoint) return 'pages-left';
    
    return 'pages-right';
  };

  const { width: bookWidth, height: bookHeight } = computeBookSize();

  // Shadow effect: synchronizes spine shadow elements height and visibility
  useEffect(() => {
    const leftShadow = document.getElementById('shadow-left-spine');
    const rightShadow = document.getElementById('shadow-right-spine');

    // Se não temos os elementos ou se as páginas ainda não estão prontas, esconde tudo.
    if (!leftShadow || !rightShadow || !pagesReady) {
      if(leftShadow) leftShadow.style.opacity = 0;
      if(rightShadow) rightShadow.style.opacity = 0;
      return;
    }

    // Regra obrigatória: se não estivermos em modo de duas páginas, não mostrar sombras
    if (!effectiveIsTwoPageMode) {
      if(leftShadow) leftShadow.style.opacity = 0;
      if(rightShadow) rightShadow.style.opacity = 0;
      return;
    }

    // **Sincroniza a altura da sombra com a altura do livro**
    try {
      leftShadow.style.height = `${bookHeight}px`;
      rightShadow.style.height = `${bookHeight}px`;
    } catch (e) {
      // ignore if DOM changes during unmount
    }

    // Verifica quantas páginas estão visíveis no DOM.
    // Se menos que 2, consideramos o livro "fechado" (capa/contracapa) e não mostramos sombras.
    try {
      const flipbookEl = containerRef.current ? containerRef.current.querySelector('.pdf-flipbook') : null;
      let visiblePages = 0;
      if (flipbookEl) {
        const pageEls = Array.from(flipbookEl.querySelectorAll('.page'));
        visiblePages = pageEls.reduce((acc, el) => {
          try {
            const rect = el.getBoundingClientRect();
            const style = window.getComputedStyle(el);
            if (style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 8 && rect.height > 8) return acc + 1;
          } catch (e) {}
          return acc;
        }, 0);
      }

      if (visiblePages < 2) {
        // Livro fechado — não mostrar sombras
        leftShadow.style.opacity = 0;
        rightShadow.style.opacity = 0;
        return;
      }
    } catch (e) {
      // ignore DOM errors
    }

    // Lógica de visibilidade (opacidade)
    const isFirstPageVisible = currentPage === 0;
    const isLastPageVisible = !effectiveIsTwoPageMode ? (currentPage === pages.length - 1) : (currentPage >= pages.length - 2);

    // Não mostra sombras internas quando o livro está em modo de 1 página
    if (!effectiveIsTwoPageMode || isSmallScreen) {
      leftShadow.style.opacity = 0;
      rightShadow.style.opacity = 0;
    } else if (isFirstPageVisible) {
      leftShadow.style.opacity = 0;
      rightShadow.style.opacity = 1;
    } else if (isLastPageVisible) {
      leftShadow.style.opacity = 1;
      rightShadow.style.opacity = 0;
    } else {
      // Livro aberto no meio (duas páginas visíveis)
      leftShadow.style.opacity = 1;
      rightShadow.style.opacity = 1;
    }

  }, [currentPage, pages, pagesReady, isSmallScreen, effectiveIsTwoPageMode, bookHeight]);

  const updateFlipbookLayout = useCallback(() => {
    const attempt = () => {
      const ref = bookRef.current;
      const pf = ref && typeof ref.pageFlip === 'function' ? ref.pageFlip() : null;
      if (pf && typeof pf.update === 'function') {
        pf.update();
        return true;
      }
      return false;
    };

    if (!attempt()) {
      requestAnimationFrame(() => attempt());
      setTimeout(() => attempt(), 200);
    }
  }, []);
  
  useEffect(() => {
    let updateTimer;
    
    if (pages.length > 0) {
      updateTimer = setTimeout(() => {
        updateFlipbookLayout();
      }, 200);
    }
    
    return () => {
      if (updateTimer) {
        clearTimeout(updateTimer);
      }
    };
  }, [viewport, pages.length, updateFlipbookLayout]);

  if (!pages.length && isLoading) {
    return (
      <div style={{
        height: `${viewport.height}px`,
        display: "flex",
        flexDirection: 'column',
        justifyContent: "center",
        alignItems: "center",
        gap: 12,
        backgroundColor,
        opacity: 1,
        transition: 'opacity 0.3s ease-in-out'
      }}>
        <div style={{
          width: "40px",
          height: "40px",
          border: "3px solid #f3f3f3",
          borderTop: "3px solid #3498db",
          borderRadius: "50%",
          animation: "spin 1s linear infinite",
          marginBottom: "1rem"
        }} aria-label="Carregando" />
        <p style={{ 
          margin: 0, 
          color: '#374151',
          fontSize: '14px',
          fontWeight: 500
        }}>Carregando PDF...</p>
      </div>
    );
  }
  const isFirstPage = currentPage === 0;
  const isLastPage = currentPage === pages.length - 1;
  
  let baseTranslate = "";
  if (effectiveIsTwoPageMode) {
    if (isFirstPage) {
      baseTranslate = `translateX(${-Math.round(bookWidth / 2)}px)`;
    } else if (isLastPage) {
      baseTranslate = `translateX(${Math.round(bookWidth / 2)}px)`;
    }
  }

  const zoomTransform = `${baseTranslate} translateX(${panOffset.x}px) translateY(${panOffset.y}px) scale(${scale})`;

  const Button = ({ onClick, style, children, disabled = false }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        ...buttonStyles.base,
        ...style,
        borderRadius: 10,
        borderWidth: 1,
        borderStyle: 'solid',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
      }}
      onTouchStart={(e) => e.currentTarget.classList.add('btn-active')}
      onTouchEnd={(e) => e.currentTarget.classList.remove('btn-active')}
    >
      {children}
    </button>
  );

  return (
    <div
      ref={containerRef}
      style={{
        backgroundColor: backgroundImage ? 'transparent' : backgroundColor,
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        padding: isFullscreen ? (isSmallScreen ? "2px" : "5px") : "10px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: `${viewport.height}px`,
        width: "100%",
        boxSizing: "border-box",
        position: "relative",
        opacity: pagesReady ? 1 : 0,
        transition: "opacity 0.3s ease-in-out",
      }}
    >
      {isLoading && <LoadingOverlay backgroundColor={backgroundColor} />}
      
      <div style={{
        marginBottom: isFullscreen ? (isSmallScreen ? "4px" : "6px") : "8px",
        display: "flex",
        gap: isSmallScreen ? "4px" : "6px",
        flexWrap: "wrap",
        justifyContent: "center",
        alignItems: "center",
      }}>
        {!isSmallScreen && (
          <div style={{ display: "flex", gap: "4px" }}>
            <Button onClick={() => bookRef.current.pageFlip().flipPrev()} style={buttonStyles.navigation}>
              ← Anterior
            </Button>
            <Button onClick={() => bookRef.current.pageFlip().flipNext()} style={buttonStyles.navigation}>
              Próxima →
            </Button>
          </div>
        )}

        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          <Button onClick={handleZoomOut} style={buttonStyles.zoom}>
            🔍−
          </Button>
          <Button
            onClick={handleZoomReset}
            style={buttonStyles.reset(scale !== 1)}
            disabled={scale === 1}
          >
            zoom Reset
          </Button>
          <Button onClick={handleZoomIn} style={buttonStyles.zoom}>
            🔍+
          </Button>
        </div>

        <Button onClick={toggleFullscreen} style={buttonStyles.fullscreen(isFullscreen)}>
          {isFullscreen ? "🗗 Sair" : "🗖 Tela Cheia"}
        </Button>
      </div>

      <div
        style={{
          flex: 1,
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
          position: "relative",
          cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
        }}
        onMouseDown={handleMouseDown}
      >
        {isSmallScreen && (
          <>
            <button
              onClick={() => bookRef.current?.pageFlip()?.flipPrev()}
              style={{
                position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', zIndex: 10,
                width: 44, height: 44, borderRadius: 9999, border: '1px solid #e5e7eb',
                background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(6px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 18, color: '#111827'
              }}
              aria-label="Página anterior"
            >
              	&larr;
            </button>
            <button
              onClick={() => bookRef.current?.pageFlip()?.flipNext()}
              style={{
                position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', zIndex: 10,
                width: 44, height: 44, borderRadius: 9999, border: '1px solid #e5e7eb',
                background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(6px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 18, color: '#111827'
              }}
              aria-label="Próxima página"
            >
             	&rarr;
            </button>
          </>
        )}
        <div 
          className={`flipbook-wrapper ${scale > 1 ? 'has-zoom' : ''} ${isDragging ? 'dragging' : ''}`} 
          style={{ overflow: 'hidden', width: '100%', height: '100%' }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >

          <HTMLFlipBook
            key={`${bookWidth}x${bookHeight}-${effectiveIsTwoPageMode ? 'two' : 'one'}`}
            width={bookWidth}
            height={bookHeight}
            showCover={true}
            drawShadow={!isSmallScreen}
            flippingTime={600}
            usePortrait={!effectiveIsTwoPageMode}
            mobileScrollSupport={true}
            maxShadowOpacity={0.4}
            swipeDistance={scale > 1 ? 9999 : (isSmallScreen ? 30 : 50)}
            clickEventForward={false}
            className={`pdf-flipbook ${getPageThicknessClass()} ${isSmallScreen ? 'mobile-mode' : ''}`}
            ref={bookRef}
            onInit={() => updateFlipbookLayout()}
            onFlip={(e) => setCurrentPage(e.data)}
            style={{
              transform: zoomTransform,
              transition: isDragging ? "none" : "transform 0.35s ease",
              transformOrigin: "center center",
              borderRadius: "8px",
              overflow: "visible",
              margin: "0 auto",
              width: bookWidth,
              height: bookHeight,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {pages.map((src, idx) => {
              const isCover = idx === 0;
              const isBackCover = idx === pages.length - 1;
              let pageClass = 'page';

              if (isCover) pageClass += ' cover-page front-cover';
              else if (isBackCover) pageClass += ' cover-page back-cover';

              // Compute pageType for data attribute used by CSS: 'cover', 'odd', or 'even'


              return (
                <div
                  key={idx}
                  className={pageClass}
                  style={{
                    backgroundColor: "#fff", display: "flex", justifyContent: "center",
                    alignItems: "center", padding: isCover || isBackCover ? "0" : "10px",
                    boxSizing: "border-box", width: '100%', height: '100%', overflow: 'hidden' 
                  }}
                >
                  <img
                    src={src}
                    alt={`Página ${idx + 1}`}
                    className="page-image"
                    style={{
                      width: '100%', height: '100%', objectFit: 'cover',
                      userSelect: "none", display: 'block', margin: '0'
                    }}
                    draggable={false}
                  />
                </div>
              );
            })}
          </HTMLFlipBook>
        </div>
      </div>

<div style={{
  margin: isFullscreen ? (isSmallScreen ? "3px 0" : "5px 0") : "8px 0",
  width: "100%", maxWidth: isSmallScreen ? "100%" : "700px",
  textAlign: "center", padding: isSmallScreen ? "0 8px" : "0 10px",
  boxSizing: "border-box", userSelect: 'none'
}}
  onMouseDown={(e) => {
    const bar = e.currentTarget.querySelector('.progress-track');
    if (!bar) return;
    setIsScrubbing(true);
    const rect = bar.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const target = Math.max(0, Math.min(pages.length - 1, Math.round(ratio * (pages.length - 1))));
    bookRef.current?.pageFlip()?.turnToPage(target);
    setCurrentPage(target);
  }}
  onMouseMove={(e) => {
    if (!isScrubbing) return;
    const bar = e.currentTarget.querySelector('.progress-track');
    if (!bar) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const target = Math.max(0, Math.min(pages.length - 1, Math.round(ratio * (pages.length - 1))));
    bookRef.current?.pageFlip()?.turnToPage(target);
    setCurrentPage(target);
  }}
  onMouseUp={() => setIsScrubbing(false)}
  onMouseLeave={() => setIsScrubbing(false)}
  onTouchStart={(e) => {
    const touch = e.touches?.[0];
    const bar = e.currentTarget.querySelector('.progress-track');
    if (!bar || !touch) return;
    setIsScrubbing(true);
    const rect = bar.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (touch.clientX - rect.left) / rect.width));
    const target = Math.max(0, Math.min(pages.length - 1, Math.round(ratio * (pages.length - 1))));
    bookRef.current?.pageFlip()?.turnToPage(target);
    setCurrentPage(target);
  }}
  onTouchMove={(e) => {
    const touch = e.touches?.[0];
    const bar = e.currentTarget.querySelector('.progress-track');
    if (!isScrubbing || !bar || !touch) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (touch.clientX - rect.left) / rect.width));
    const target = Math.max(0, Math.min(pages.length - 1, Math.round(ratio * (pages.length - 1))));
    bookRef.current?.pageFlip()?.turnToPage(target);
    setCurrentPage(target);
  }}
  onTouchEnd={() => setIsScrubbing(false)}
>
  <div
    className="progress-track"
    style={{
      width: "100%",
      height: isSmallScreen ? "10px" : "12px",
      background: "#f1f5f9",
      borderRadius: isSmallScreen ? "6px" : "8px",
      overflow: "hidden",
      marginBottom: isSmallScreen ? "4px" : "6px",
      boxShadow: "inset 0 1px 2px rgba(0,0,0,0.05)",
      border: "1px solid #e2e8f0",
      cursor: 'pointer',
      position: 'relative'
    }}
  >
    <div style={{
      width: `${((currentPage + 1) / pages.length) * 100}%`,
      height: "100%",
      background: `linear-gradient(90deg, #00B2A9 0%, #002F6C 100%)`,
      borderRadius: isSmallScreen ? "6px" : "8px",
      transition: isScrubbing ? 'none' : "width 0.25s ease",
      boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
      position: "relative"
    }}>
      <div style={{
        position: "absolute",
        top: '50%',
        right: 0,
        width: isSmallScreen ? 14 : 16,
        height: isSmallScreen ? 14 : 16,
        transform: 'translate(50%, -50%)',
        background: '#ffffff',
        borderRadius: '50%',
        border: '2px solid #00B2A9',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }} />
    </div>

    <div style={{
      position: 'absolute',
      inset: 0,
      display: 'flex',
      justifyContent: 'space-between',
      pointerEvents: 'none',
      opacity: 0.1
    }}>
      {Array.from({ length: Math.min(10, pages.length - 1) }).map((_, i) => (
        <div key={i} style={{ width: 1, background: '#002F6C22', height: '100%' }} />
      ))}
    </div>
  </div>

  <div style={{
    fontSize: isSmallScreen ? "12px" : "13px",
    color: "#64748b",
    fontWeight: 500,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  }}>
    <span>
      Página <strong>{currentPage + 1}</strong> de <strong>{pages.length}</strong>
    </span>
    {scale > 1 && !isSmallScreen && (
      <span style={{ color: "#00B2A9", fontSize: "12px" }}>
        Zoom: {Math.round(scale * 100)}% • Arraste para mover
      </span>
    )}
  </div>
</div>
</div>
  );
};

export default FlipbookViewer;